import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  DataGrid, DataGridHeader, DataGridHeaderCell, DataGridBody, DataGridRow, DataGridCell,
  TableColumnDefinition, createTableColumn,
  Button, Input, Badge, Avatar, Spinner, Text,
  makeStyles, tokens,
} from '@fluentui/react-components'
import { SearchRegular } from '@fluentui/react-icons'
import { employeesService } from '../services/employeesService'
import { contractorsService, type ContractorListItem } from '../services/contractorsService'

interface PickerPerson {
  rowId: string               // "emp:5" | "con:5" — unikátní přes oba typy
  personType: 'employee' | 'contractor'
  id: number
  displayName: string
  avatarName: string          // pro Avatar initials
  email: string
  departmentName: string | null
}

interface AssignPersonDialogProps {
  open: boolean
  onDismiss: () => void
  onAssign: (employeeIds: number[], contractorIds: number[]) => Promise<void>
  alreadyAssignedEmployeeIds: Set<number>
  alreadyAssignedContractorIds: Set<number>
  /** Orbit emaily všech již přiřazených osob — pro dedup externistů se stejným emailem. */
  alreadyAssignedEmails: Set<string>
}

function contractorDisplayName(con: ContractorListItem): string {
  const contactFull = `${con.contactFirstName} ${con.contactLastName}`.trim()
  const nameLower = con.companyName.toLowerCase()
  const nameInCompany = nameLower.includes(con.contactFirstName.toLowerCase())
    && nameLower.includes(con.contactLastName.toLowerCase())
  return nameInCompany ? contactFull : `${contactFull} (${con.companyName})`
}

const useStyles = makeStyles({
  filterRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    paddingBottom: tokens.spacingVerticalS,
    flexWrap: 'wrap',
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  grid: { maxHeight: '400px', overflowY: 'auto' },
  nameCell: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
})

export default function AssignPersonDialog({
  open, onDismiss, onAssign,
  alreadyAssignedEmployeeIds, alreadyAssignedContractorIds, alreadyAssignedEmails,
}: AssignPersonDialogProps) {
  const styles = useStyles()
  const [search, setSearch] = useState('')
  const [deptFilter, setDeptFilter] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<'all' | 'employee' | 'contractor'>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)

  const { data: employees = [], isLoading: loadingEmp, isError: errorEmp, refetch: refetchEmp } = useQuery({
    queryKey: ['employees-assign-picker'],
    queryFn: () => employeesService.getAll({ status: 1, pageSize: 500 }).then(r =>
      r.items.filter(e => e.hasEntraId)
    ),
    enabled: open,
    staleTime: 60_000,
  })

  const { data: contractors = [], isLoading: loadingCon, isError: errorCon, refetch: refetchCon } = useQuery({
    queryKey: ['contractors-assign-picker'],
    queryFn: () => contractorsService.getAll({ isActive: true, pageSize: 500 }).then(r => r.items),
    enabled: open,
    staleTime: 60_000,
  })

  const isLoading = loadingEmp || loadingCon
  const isError = errorEmp || errorCon

  // Sloučit oba zdroje do PickerPerson[], vyloučit již přiřazené
  const allPersons = useMemo<PickerPerson[]>(() => {
    const empItems: PickerPerson[] = employees
      .filter(e => !alreadyAssignedEmployeeIds.has(e.id))
      .map(e => ({
        rowId: `emp:${e.id}`,
        personType: 'employee',
        id: e.id,
        displayName: e.fullName,
        avatarName: e.fullName,
        email: e.email,
        departmentName: e.departmentName,
      }))

    // Orbit emaily pro dedup: aktivní zaměstnanci z pickeru + všechny již přiřazené osoby
    // (pokrývá i ukončené zaměstnance, kteří nejsou v aktivním pickeru).
    // Pokud stejný orbit email existuje, externista se nezobrazí — zaměstnanec má přednost.
    const allEmployeeEmails = new Set([
      ...employees.map(e => e.email.toLowerCase()),
      ...alreadyAssignedEmails,
    ])

    const conItems: PickerPerson[] = contractors
      .filter(c =>
        !alreadyAssignedContractorIds.has(c.id) &&
        !!c.orbitEmail &&
        !allEmployeeEmails.has(c.orbitEmail.toLowerCase())
      )
      .map(c => ({
        rowId: `con:${c.id}`,
        personType: 'contractor',
        id: c.id,
        displayName: contractorDisplayName(c),
        avatarName: `${c.contactFirstName} ${c.contactLastName}`,
        email: c.orbitEmail ?? '',
        departmentName: c.departmentName,
      }))

    return [...empItems, ...conItems].sort((a, b) => a.displayName.localeCompare(b.displayName, 'cs'))
  }, [employees, contractors, alreadyAssignedEmployeeIds, alreadyAssignedContractorIds])

  const departments = useMemo(() => {
    const set = new Set(allPersons.map(p => p.departmentName).filter(Boolean) as string[])
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'cs'))
  }, [allPersons])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return allPersons.filter(p => {
      const matchSearch = !q || p.displayName.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)
      const matchDept = !deptFilter || p.departmentName === deptFilter
      const matchType = typeFilter === 'all' || p.personType === typeFilter
      return matchSearch && matchDept && matchType
    })
  }, [allPersons, search, deptFilter, typeFilter])

  const filteredIds = useMemo(() => new Set(filtered.map(p => p.rowId)), [filtered])

  const visibleSelectedIds = useMemo(
    () => new Set([...selectedIds].filter(id => filteredIds.has(id))),
    [selectedIds, filteredIds],
  )

  const columns: TableColumnDefinition<PickerPerson>[] = useMemo(() => [
    createTableColumn<PickerPerson>({
      columnId: 'name',
      compare: (a, b) => a.displayName.localeCompare(b.displayName, 'cs'),
      renderHeaderCell: () => 'Jméno',
      renderCell: (item) => (
        <div className={styles.nameCell}>
          <Avatar name={item.avatarName} color="colorful" size={24} />
          {item.displayName}
        </div>
      ),
    }),
    createTableColumn<PickerPerson>({
      columnId: 'email',
      compare: (a, b) => a.email.localeCompare(b.email),
      renderHeaderCell: () => 'E-mail',
      renderCell: (item) => (
        <span style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{item.email}</span>
      ),
    }),
    createTableColumn<PickerPerson>({
      columnId: 'department',
      compare: (a, b) => (a.departmentName ?? '').localeCompare(b.departmentName ?? '', 'cs'),
      renderHeaderCell: () => 'Oddělení',
      renderCell: (item) => item.departmentName
        ? <Badge appearance="outline" size="small">{item.departmentName}</Badge>
        : null,
    }),
    createTableColumn<PickerPerson>({
      columnId: 'type',
      compare: (a, b) => a.personType.localeCompare(b.personType),
      renderHeaderCell: () => 'Typ',
      renderCell: (item) => item.personType === 'employee' ? (
        <Badge
          appearance="outline"
          size="small"
          style={{ color: 'rgb(255, 102, 0)', borderColor: 'rgb(255, 102, 0)' }}
        >
          Zaměstnanec
        </Badge>
      ) : (
        <Badge appearance="outline" color="informative" size="small">
          Externista
        </Badge>
      ),
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [styles.nameCell])

  function handleSelectionChange(_: unknown, data: { selectedItems: Set<string | number> }) {
    const next = data.selectedItems as Set<string>
    // Zachovat výběr mimo aktuální filtrovaný pohled
    const preserved = new Set([...selectedIds].filter(id => !filteredIds.has(id)))
    setSelectedIds(new Set([...preserved, ...next]))
  }

  async function handleAssign() {
    setSubmitting(true)
    try {
      const empIds = [...selectedIds].filter(id => id.startsWith('emp:')).map(id => Number(id.slice(4)))
      const conIds = [...selectedIds].filter(id => id.startsWith('con:')).map(id => Number(id.slice(4)))
      await onAssign(empIds, conIds)
      resetState()
    } finally {
      setSubmitting(false)
    }
  }

  function resetState() {
    setSelectedIds(new Set())
    setSearch('')
    setDeptFilter(null)
    setTypeFilter('all')
  }

  function handleClose() {
    resetState()
    onDismiss()
  }

  const alreadyCount =
    (employees.length - employees.filter(e => !alreadyAssignedEmployeeIds.has(e.id)).length) +
    (contractors.length - contractors.filter(c => !alreadyAssignedContractorIds.has(c.id)).length)

  const selectedEmpCount = [...selectedIds].filter(id => id.startsWith('emp:')).length
  const selectedConCount = [...selectedIds].filter(id => id.startsWith('con:')).length

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) handleClose() }}>
      <DialogSurface style={{ width: '780px', maxWidth: '95vw' }}>
        <DialogTitle>
          Přiřadit osoby
          {selectedIds.size > 0 && (
            <Badge appearance="filled" color="brand" style={{ marginLeft: '8px' }}>
              Vybráno: {selectedIds.size}
            </Badge>
          )}
        </DialogTitle>
        <DialogBody>
          <DialogContent>
            {/* Vyhledávání */}
            <Input
              contentBefore={<SearchRegular />}
              placeholder="Hledat jméno, firmu nebo e-mail…"
              value={search}
              onChange={(_, d) => setSearch(d.value)}
              style={{ width: '100%', marginBottom: tokens.spacingVerticalS }}
            />

            {/* Filtry: Typ osoby */}
            <div className={styles.filterRow}>
              {([
                ['all', 'Všichni'],
                ['employee', 'Zaměstnanci'],
                ['contractor', 'Externisté'],
              ] as const).map(([val, label]) => (
                <Button
                  key={val}
                  size="small"
                  appearance={typeFilter === val ? 'primary' : 'subtle'}
                  onClick={() => setTypeFilter(val)}
                >
                  {label}
                </Button>
              ))}

              {/* Filtry: Oddělení */}
              {departments.length > 0 && (
                <>
                  <span style={{ alignSelf: 'center', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, margin: `0 ${tokens.spacingHorizontalXS}` }}>|</span>
                  <Button
                    size="small"
                    appearance={deptFilter === null ? 'primary' : 'subtle'}
                    onClick={() => setDeptFilter(null)}
                  >
                    Všechna oddělení
                  </Button>
                  {departments.map(dept => (
                    <Button
                      key={dept}
                      size="small"
                      appearance={deptFilter === dept ? 'primary' : 'subtle'}
                      onClick={() => setDeptFilter(d => d === dept ? null : dept)}
                    >
                      {dept}
                    </Button>
                  ))}
                </>
              )}
            </div>

            {/* Meta řádek */}
            <div className={styles.metaRow}>
              <span>
                Zobrazeno {filtered.length} z {allPersons.length}
                {alreadyCount > 0 && ` · ${alreadyCount} již přiřazeno`}
              </span>
              {selectedIds.size > 0 && (
                <span>
                  Vybráno: {selectedIds.size}
                  {selectedEmpCount > 0 && selectedConCount > 0 && ` (${selectedEmpCount} zam. + ${selectedConCount} ext.)`}
                </span>
              )}
            </div>

            {/* DataGrid */}
            {isLoading ? (
              <Spinner label="Načítám…" />
            ) : isError ? (
              <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
                Nepodařilo se načíst data.{' '}
                <Button appearance="transparent" size="small" onClick={() => { refetchEmp(); refetchCon() }}>
                  Zkusit znovu
                </Button>
              </Text>
            ) : filtered.length === 0 ? (
              <Text style={{ color: tokens.colorNeutralForeground3 }}>
                Žádné osoby neodpovídají zadaným filtrům.
              </Text>
            ) : (
              <div className={styles.grid}>
                <DataGrid
                  items={filtered}
                  columns={columns}
                  selectionMode="multiselect"
                  selectedItems={visibleSelectedIds}
                  onSelectionChange={handleSelectionChange}
                  getRowId={(item) => item.rowId}
                  sortable
                  focusMode="composite"
                >
                  <DataGridHeader>
                    <DataGridRow selectionCell={{ checkboxIndicator: { 'aria-label': 'Vybrat vše' } }}>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<PickerPerson>>
                    {({ item, rowId }) => (
                      <DataGridRow<PickerPerson>
                        key={rowId}
                        selectionCell={{ checkboxIndicator: { 'aria-label': `Vybrat ${item.displayName}` } }}
                      >
                        {({ renderCell }) => (
                          <DataGridCell>{renderCell(item)}</DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              appearance="subtle"
              disabled={selectedIds.size === 0}
              onClick={() => setSelectedIds(new Set())}
            >
              Zrušit výběr
            </Button>
            <Button appearance="secondary" onClick={handleClose}>
              Zavřít
            </Button>
            <Button
              appearance="primary"
              disabled={selectedIds.size === 0 || submitting}
              icon={submitting ? <Spinner size="tiny" /> : undefined}
              onClick={handleAssign}
            >
              {submitting ? 'Přiřazuji…' : `Přiřadit (${selectedIds.size})`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
